import { ElementType } from 'react';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import PeopleIcon from '@mui/icons-material/People';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { BusinessOutlined, WifiProtectedSetupOutlined } from '@mui/icons-material';
import { USER_TYPES } from '../../services/user';
import User from '../../interfaces/users.interface';

export interface NavDestination {
  id: string;
  label: string;
  icon: ElementType;
  /** Internal route (react-router). Present for every destination except externals. */
  to?: string;
  /** External URL — opens in a new tab and never shows an active state. */
  href?: string;
  external?: boolean;
}

// Top-level destinations only. The former Profile sub-items (Services, Sites,
// Applications, Certifications, Manage Assessments, Documents) move to the
// Part 2 sub-nav and are intentionally absent here.
const DESTINATIONS: Record<string, NavDestination> = {
  dashboard: { id: 'dashboard', label: 'Dashboard', icon: SpeedOutlinedIcon, to: '/dashboard' },
  profile: { id: 'profile', label: 'Profile', icon: AccountCircleOutlinedIcon, to: '/profile' },
  companies: { id: 'companies', label: 'Companies', icon: BusinessOutlined, to: '/registry' },
  applications: { id: 'applications', label: 'Applications', icon: AssessmentOutlinedIcon, to: '/applications' },
  exchange: { id: 'exchange', label: 'TPN Exchange', icon: WifiProtectedSetupOutlined, to: '/trusted-partner-exchange' },
  resources: { id: 'resources', label: 'Partner Resources', icon: LocalLibraryIcon, to: '/partner-resources' },
  howto: { id: 'howto', label: 'How-To Guides', icon: MenuBookIcon, href: 'https://www.ttpn.org/links-resources/', external: true },
  admin: { id: 'admin', label: 'Admin', icon: PeopleIcon, to: '/admin/users' },
};

/**
 * Ordered top-level destinations for the icon rail, derived from `user.type`.
 * How-To Guides is always the last entry, except Admin (TPN Admin only) which
 * always follows it. Mirrors the pre-redesign role gating: Vendor Basic shares
 * the `vendor_admin` type, so it sees the same set as an SP Vendor Admin.
 */
export function getNavItems(user?: User): NavDestination[] {
  const type = user?.type;
  if (!type) return [];

  const d = DESTINATIONS;

  if (type === USER_TYPES.TPN_ADMIN) {
    return [d.dashboard, d.companies, d.applications, d.exchange, d.resources, d.howto, d.admin];
  }

  const items: NavDestination[] = [];
  // Dashboard shows for a Content Owner only once their company is published.
  if (type === USER_TYPES.CONTENT_OWNER && user?.companies?.[0]?.isPublished) {
    items.push(d.dashboard);
  }
  items.push(d.profile, d.companies, d.applications);
  // TPN Exchange is not available to Assessor or Content Owner.
  if (type === USER_TYPES.VENDOR_ADMIN) {
    items.push(d.exchange);
  }
  items.push(d.resources, d.howto);
  return items;
}

/** First path segment, e.g. "/profile/sites" -> "profile". */
const firstSegment = (path?: string) => (path ? path.split('/')[1] || '' : '');

/**
 * The internal destination whose route matches the current pathname's first
 * segment, if any. External items (no `to`) and non-destination routes
 * (e.g. `/user-profile`) return `undefined`.
 */
export function getActiveNavItem(items: NavDestination[], pathname: string): NavDestination | undefined {
  const seg = firstSegment(pathname);
  return items.find((item) => item.to && firstSegment(item.to) === seg);
}
